import React, {
  useEffect,
  useState,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from "react";
import ReactFlow, { Controls, Edge, Node } from "reactflow";
import { Button } from "reactstrap";
import { useForm, FieldErrors } from "react-hook-form";
import "reactflow/dist/style.css";
import Reason from "common/utilities/Reason/Reason";
import { showToastMessage } from "common/service/CommonService";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import StageNode from "../components/StageNode";
import AddStageNode from "../components/AddStageNode";
import ReturnEdge from "../components/ReturnEdge";
import NextEdge from "../components/NextEdge";
import { EdgeMeta, EditStage, PopoverPosition, Stage, WorkflowPreviewRef } from "../helpers/workflow.types";
import { validations } from "validations/FormValidations";

/* =====================================================
   Types
===================================================== */

interface WorkflowPreviewProps {
  stages: Stage[];
  onEditStage: (stage: EditStage) => void;
  onDeleteStage: (stage: EditStage) => void;
  setStages: React.Dispatch<React.SetStateAction<Stage[]>>;
  onAddStage: () => void; // 👈 NEW
  readOnly?: boolean; // ✅ ADD
}

/* =====================================================
   Node & Edge Types
===================================================== */

const nodeTypes = {
  stageNode: StageNode,
  addStageNode: AddStageNode,
};

const edgeTypes = {
  returnEdge: ReturnEdge,
  nextEdge: NextEdge,
};

/* =====================================================
   Component
===================================================== */

const WorkflowPreview = forwardRef<WorkflowPreviewRef, WorkflowPreviewProps>(
  (
    {
      stages,
      onEditStage,
      onDeleteStage,
      setStages,
      onAddStage,
      readOnly = false,
    },
    ref,
  ) => {
    const [edges, setEdges] = useState<Edge[]>([]);
    const [editingEdgeMeta, setEditingEdgeMeta] = useState<EdgeMeta | null>(
      null,
    );
    const [popoverPos, setPopoverPos] = useState<PopoverPosition | null>(null);

    /* ---------- RHF for edge popover ---------- */
    const {
      control: edgeControl,
      handleSubmit: handleEdgeSubmit,
      reset: resetEdgeForm,
      getValues: getEdgeValues,
      formState: { errors: edgeErrors },
    } = useForm<{ actionLabel: string; status: string }>({
      defaultValues: { actionLabel: "", status: "" },
      mode: "onChange",
    });

    const edgeFormFields: FormFieldConfig[] = [
      {
        name: "actionLabel",
        label: "Action Label",
        type: "text",
        placeholder: "Enter action label",
        required: true,
        rules: {
          validate: (value: string) => {
            const result = validations.workflow.actionlabel.validate(value);
            return result === true ? true : result;
          },
        },
      },
      {
        name: "status",
        label: "Status",
        type: "text",
        placeholder: "Enter status",
        required: true,
        rules: {
          validate: (value: string) => {
            const result = validations.workflow.status.validate(value);
            return result === true ? true : result;
          },
        },
      },
    ];

    //Reason Modal
    const [isReasonModal, setIsReasonModal] = useState<boolean>(false);
    const [transitionId, setTransitionId] = useState<number | null>(null);
    const [pendingEdgeSave, setPendingEdgeSave] = useState<EdgeMeta | null>(
      null,
    );
    //toggles
    const toggleReasonModal = (): void => {
      setIsReasonModal((prev) => !prev);
    };

    const POPOVER_WIDTH = 260;
    const POPOVER_HEIGHT = 220;
    const POPOVER_PADDING = 12;

    /* ---------- STAGE ID SET ---------- */
    const stageIds = useMemo(
      () => new Set(stages.map((s) => s.stageId.toString())),
      [stages],
    );

    /* ---------- BUILD EDGES ---------- */
    useEffect(() => {
      let returnCounter = 0;

      /* ---------- NEXT EDGES ---------- */
      const forwardEdges: Edge[] = stages
        .filter((s) => !s.isDeleted)
        .slice(0, -1)
        .flatMap((stage, index) => {
          const nextStage = stages.filter((s) => !s.isDeleted)[index + 1];
          if (!nextStage) return [];

          const nextTransition = stage.transitions?.next;

          return [
            {
              id: `next-${stage.stageId}-${nextStage.stageId}`,
              type: "nextEdge",
              source: stage.stageId.toString(),
              target: nextStage.stageId.toString(),
              data: {
                id: nextTransition?.id,
                type: "next",
                fromStageId: stage.stageId,
                toStageId: nextStage.stageId,
                actionLabel: nextTransition?.actionLabel || "NEXT",
                status: nextTransition?.status || "",
                // onEdit: readOnly ? undefined : handleEditEdge,
                onEdit: handleEditEdge,
              },
            },
          ];
        });

      /* ---------- RETURN EDGES ---------- */
      const returnEdges: Edge[] = stages
        .filter((s) => !s.isDeleted)
        .flatMap((fromStage) => {
          const toStageId = fromStage.returnTo;
          if (!toStageId) return [];

          if (
            !stageIds.has(toStageId.toString()) ||
            toStageId === fromStage.stageId
          ) {
            return [];
          }

          const isLeft = returnCounter % 2 === 0;
          const lane = Math.floor(returnCounter / 2);
          returnCounter++;

          const ret = fromStage.transitions?.returns?.find(
            (r) => r.toStageId === toStageId,
          );

          return [
            {
              id: `return-${fromStage.stageId}-${toStageId}`,
              type: "returnEdge",
              source: fromStage.stageId.toString(),
              target: toStageId.toString(),
              sourceHandle: isLeft ? "return-left-src" : "return-right-src",
              targetHandle: isLeft ? "return-left-tgt" : "return-right-tgt",
              data: {
                id: ret?.id,
                type: "return",
                fromStageId: fromStage.stageId,
                toStageId,
                actionLabel: ret?.actionLabel || "RETURN",
                status: ret?.status || "",
                direction: isLeft ? "left" : "right",
                lane,
                // onEdit: readOnly ? undefined : handleEditEdge,
                onEdit: handleEditEdge,
              },
            },
          ];
        });

      setEdges([...forwardEdges, ...returnEdges]);
    }, [stages]);

    /* ---------- EDGE EDIT HANDLER ---------- */
    const handleEditEdge = (edgeId: string, meta: EdgeMeta): void => {
      const stage = stages
        .filter((s) => !s.isDeleted)
        .find((s) => s.stageId === meta.fromStageId);
      if (!stage) return;

      let label = "";
      let stat = "";

      if (meta.type === "next") {
        label = stage.transitions?.next?.actionLabel || "";
        stat = stage.transitions?.next?.status || "";
      }

      if (meta.type === "return") {
        const ret = stage.transitions?.returns?.find(
          (r) => r.toStageId === meta.toStageId,
        );
        label = ret?.actionLabel || "";
        stat = ret?.status || "";
      }

      setEditingEdgeMeta(meta);
      resetEdgeForm({ actionLabel: label, status: stat });
      openPopover(meta);
    };

    const validateWorkflowTransitions = () => {
      const activeStages = stages.filter((s) => !s.isDeleted);

      for (let i = 0; i < activeStages.length; i++) {
        const stage = activeStages[i];
        const isLastStage = i === activeStages.length - 1;

        const next = stage.transitions?.next;

        // ❗ SKIP NEXT validation for LAST stage
        if (!isLastStage) {
          if (!next) {
            showToastMessage(
              "error",
              `Stage "${stage.stageName}" → NEXT transition is required`,
            );
            return false;
          }

          if (!next.actionLabel?.trim() || !next.status?.trim()) {
            showToastMessage(
              "error",
              `Stage "${stage.stageName}" → Action Label & Status required`,
            );
            return false;
          }
        }

        // ❗ RETURNS (optional but validate if exists)
        const returns = stage.transitions?.returns;

        // ✅ Validate ONLY if returnTo exists
        if (stage.returnTo) {
          if (!returns || returns.length === 0) {
            showToastMessage(
              "error",
              `Stage "${stage.stageName}" → Return transition is required`,
            );
            return false;
          }

          for (const ret of returns) {
            if (!ret.actionLabel?.trim() || !ret.status?.trim()) {
              showToastMessage(
                "error",
                `Stage "${stage.stageName}" → Return Action & Status required`,
              );
              return false;
            }
          }
        }
      }

      return true;
    };

    /* ---------- SAVE EDGE ACTION ---------- */
    const applyEdgeSave = (meta: EdgeMeta, values: { actionLabel: string; status: string }, updatereason?: string) => {
      setStages((prev) =>
        prev.map((s) => {
          if (s.stageId !== meta.fromStageId) return s;

          // -------- NEXT --------
          if (meta.type === "next") {
            return {
              ...s,
              transitions: {
                ...s.transitions,
                next: {
                  ...(s.transitions?.next?.id && {
                    id: s.transitions.next.id,
                  }),
                  toStageId: meta.toStageId,
                  actionLabel: values.actionLabel,
                  status: values.status,
                  updatereason,
                },
              },
            };
          }

          // -------- RETURN --------
          if (meta.type === "return") {
            const returns = s.transitions?.returns ?? [];

            const exists = returns.some((r) => r.toStageId === meta.toStageId);

            const updatedReturns = exists
              ? returns.map((r) =>
                r.toStageId === meta.toStageId
                  ? { ...r, actionLabel: values.actionLabel, status: values.status, updatereason }
                  : r
              )
              : [
                ...returns,
                {
                  toStageId: meta.toStageId,
                  actionLabel: values.actionLabel,
                  status: values.status,
                  updatereason,
                },
              ];

            return {
              ...s,
              transitions: {
                ...s.transitions,
                returns: updatedReturns,
              },
            };
          }

          return s;
        })
      );

      // cleanup
      setEditingEdgeMeta(null);
      setPopoverPos(null);
      setPendingEdgeSave(null);
      resetEdgeForm({ actionLabel: "", status: "" });
    };

    const btnReasonClick = (payload: { id: number | null; reason: string }) => {
      if (!pendingEdgeSave) return;

      const values = getEdgeValues();
      applyEdgeSave(pendingEdgeSave, values, payload.reason);
      setPendingEdgeSave(null);
      toggleReasonModal();
    };

    const saveEdgeAction = handleEdgeSubmit((values) => {
      if (!editingEdgeMeta) return;

      const stage = stages.find(
        (s) => s.stageId === editingEdgeMeta.fromStageId,
      );
      if (!stage) return;

      let existingId: number | null = null;

      // -------- NEXT --------
      if (editingEdgeMeta.type === "next") {
        existingId = stage.transitions?.next?.id ?? null;
      }

      // -------- RETURN --------
      if (editingEdgeMeta.type === "return") {
        existingId =
          stage.transitions?.returns?.find(
            (r) => r.toStageId === editingEdgeMeta.toStageId,
          )?.id ?? null;
      }

      // UPDATE → REQUIRE REASON
      if (existingId) {
        setPendingEdgeSave(editingEdgeMeta);
        toggleReasonModal();
        return;
      }

      // NEW → SAVE DIRECTLY
      applyEdgeSave(editingEdgeMeta, values);
    });

    useImperativeHandle(ref, () => ({
      validateEdges: validateWorkflowTransitions,
    }));
   
    const nodes: Node[] = [
      ...stages
        .filter((s) => !s.isDeleted)
        .map((stage, index) => ({
          id: stage.stageId.toString(),
          type: "stageNode",
          data: {
            stage,
            index,
            onEdit: onEditStage,
            onDelete: onDeleteStage,
            readOnly,
          },
          position: { x: 150, y: index * 140 },
        })),
    ];

    const finalNodes = readOnly
      ? nodes
      : [
        ...nodes,
        {
          id: "add-stage",
          type: "addStageNode",
          data: { onAdd: onAddStage },
          position: {
            x: 150,
            y: stages.filter((s) => !s.isDeleted).length * 140,
          },
        },
      ];

    /* ---------- POPOVER POSITION ---------- */
    const openPopover = (meta: EdgeMeta): void => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let x = meta.x + 8;
      let y = meta.y + 8;

      if (x + POPOVER_WIDTH > viewportWidth) {
        x = viewportWidth - POPOVER_WIDTH - POPOVER_PADDING;
      }

      if (y + POPOVER_HEIGHT > viewportHeight) {
        y = viewportHeight - POPOVER_HEIGHT - POPOVER_PADDING;
      }

      setPopoverPos({
        x: Math.max(POPOVER_PADDING, x),
        y: Math.max(POPOVER_PADDING, y),
      });
    };

    /* =====================================================
     Render
  ===================================================== */

    return (
      <>
        <div className="card h-100 p-2 mb-0">
          <h6 className="mb-2">Workflow Preview</h6>

          <div className="h-100">
            <ReactFlow
              nodes={finalNodes}
              edges={edges}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              fitView
              nodesDraggable={false}
              nodesConnectable={false}
            >
              <Controls />
            </ReactFlow>
          </div>
        </div>

        {/* ---------- POPOVER ---------- */}
        {editingEdgeMeta && popoverPos && (
          <div
            style={{
              position: "fixed",
              top: popoverPos.y + 8,
              left: popoverPos.x + 8,
              zIndex: 1000,
              background: "#fff",
              borderRadius: 6,
              boxShadow: "0 4px 12px rgba(0,0,0,.15)",
              padding: 12,
              width: 340,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <CommonForm
              fields={edgeFormFields}
              control={edgeControl}
              errors={edgeErrors as FieldErrors}
              columns={1}
            />

            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button
                size="sm"
                color="secondary"
                onClick={() => {
                  setEditingEdgeMeta(null);
                  setPopoverPos(null);
                  resetEdgeForm({ actionLabel: "", status: "" });
                }}
              >
                Cancel
              </Button>
              {!readOnly && (
                <Button size="sm" color="primary" onClick={saveEdgeAction}>
                  Save
                </Button>
              )}
            </div>
          </div>
        )}

        {isReasonModal && (
          <Reason
            isOpen={isReasonModal}
            toggle={toggleReasonModal}
            action="Update"
            id={transitionId}
            click={btnReasonClick}
            isLoading={false}
          />
        )}
      </>
    );
  },
);

export default WorkflowPreview;
